import axios from "axios";
import { decodeToken } from "react-jwt";
import { Jwt, UserType } from "../userTypes.ts";
import { getAvatarsBaseUrl, getBackendMainUrl } from "./global";
import { FamilyMember } from "@/features/Login/Family/types.ts";

const BASE_URL_API = getBackendMainUrl + "/users";
const HEADERS = {
  headers: {
    "Content-Type": "multipart/form-data",
    authorization: localStorage.getItem("authorization"),
  },
};

// Utilitaires de base
export const getUserFromId = (userId: string, userList: UserType[]) =>
  userList.find((u) => u._id === userId);

export const getUserById = async (userId: string): Promise<UserType | null> => {
  if (typeof userId !== "string" || !userId.trim()) {
    console.error("getUserById a reçu un ID invalide:", userId);
    return null;
  }

  try {
    const response = await axios.get(BASE_URL_API + "/" + userId, {
      headers: { authorization: localStorage.getItem("authorization") },
    });
    return response.data;
  } catch (error) {
    console.error(
      `Erreur lors de la récupération de l'utilisateur ${userId}:`,
      error
    );
    return null;
  }
};

export const getUserLogged = () => {
  const authItem = localStorage.getItem("authorization");
  if (!authItem) return null;
  const decodedToken: Jwt | undefined =
    decodeToken(authItem.split(" ")[1]) || undefined;
  return decodedToken?.user || null;
};

export const setAuthToken = (token: string) => {
  localStorage.setItem("authorization", "Bearer " + token);
};

export const clearUserLogged = () => {
  localStorage.removeItem("authorization");
};

// Auth
export const submitLogin = async (userForm: {
  login: string;
  password: string;
}) => {
  localStorage.clear();
  try {
    const result = await axios.post(BASE_URL_API + "/login", userForm);
    if (result.data.message) {
      console.error(result.data);
      return;
    }

    // Stocker le token
    setAuthToken(result.data);
    return result;
  } catch (err: Error | unknown) {
    console.log("err: ", (err as Error).message);
    return (err as Error).message;
  }
};

export const sendRegisterForm = async (registerForm: FormData) => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/register",
      registerForm,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );
    return response.data;
  } catch (err: Error | unknown) {
    console.log("err: ", (err as Error).message);
    throw err;
  }
};

export const getAvatarUrl = (avatarUri: string | undefined) => {
  if (!avatarUri) {
    return "";
  }
  // Normalisation URL - S'assurer que baseUrl se termine par un slash
  const baseUrl = getAvatarsBaseUrl.replace("/images/avatar", "/");
  const normalizedBaseUrl = baseUrl.endsWith("/") ? baseUrl : baseUrl + "/";
  let resultUrl = "";
  // Vérifier si l'URI contient déjà "images/avatar"
  if (avatarUri.startsWith("images/avatar/")) {
    resultUrl = normalizedBaseUrl + avatarUri;
  } else if (avatarUri.includes("images/avatar/images/avatar/")) {
    const correctedUri = avatarUri.replace(
      "images/avatar/images/avatar/",
      "images/avatar/"
    );
    resultUrl = normalizedBaseUrl + correctedUri;
  } else {
    resultUrl = normalizedBaseUrl + "images/avatar/" + avatarUri;
  }
  return resultUrl;
};

export const getUserList = async () => {
  try {
    const response = await axios.get(BASE_URL_API + "/userList", {
      headers: { authorization: localStorage.getItem("authorization") },
    });
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération de la liste d'utilisateurs:",
      error
    );
    throw error;
  }
};

export const getSpecifiedUserList = async (userList: string[]) => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/getUsersList",
      userList,
      HEADERS
    );
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des utilisateurs spécifiques:",
      error
    );
    throw error;
  }
};

export const getUserListExceptUserLogged = async () => {
  const userList = await getUserList();
  const currentUser = getUserLogged();
  return userList.filter((user: UserType) => user._id !== currentUser?._id);
};

export const createFamily = async (familyData: {
  name: string;
  owner: string;
}) => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/createFamily",
      familyData,
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la création de la famille:", error);
    throw error;
  }
};

export const joinFamily = async (inviteCode: string) => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/joinFamily",
      { inviteCode },
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la rejointe d'une famille:", error);
    throw error;
  }
};

export const leaveFamily = async () => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/leaveFamily",
      {},
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du départ de la famille:", error);
    throw error;
  }
};

export const getFamilyDetails = async () => {
  try {
    const response = await axios.get(BASE_URL_API + "/family/details", HEADERS);
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la récupération des détails de la famille:",
      error
    );
    throw error;
  }
};

export const regenerateInviteCode = async () => {
  try {
    const response = await axios.post(
      BASE_URL_API + "/family/regenerateInviteCode",
      {},
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error(
      "Erreur lors de la régénération du code d'invitation:",
      error
    );
    throw error;
  }
};

export const addHouse = async (houseData: {
  name: string;
  address?: string;
}) => {
  try {
    const response = await axios.post(BASE_URL_API + "/house", houseData, {
      headers: { authorization: localStorage.getItem("authorization") },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de l'ajout d'une maison:", error);
    throw error;
  }
};

export const removeHouse = async (houseId: string) => {
  try {
    const response = await axios.delete(BASE_URL_API + "/house/" + houseId, {
      headers: { authorization: localStorage.getItem("authorization") },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la suppression d'une maison:", error);
    throw error;
  }
};

/**
 * Retire un membre de la famille
 * @param memberId ID du membre à retirer
 * @param familyId ID de la famille
 * @returns Message de confirmation
 */
export const removeFamilyMember = async (
  memberId: string,
  familyId: string
) => {
  try {
    const response = await axios.delete(
      `${BASE_URL_API}/family/${familyId}/remove/${memberId}`,
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du retrait d'un membre:", error);
    throw error;
  }
};

/**
 * Transfère la propriété de la famille à un autre membre
 * @param newOwnerId ID du nouveau propriétaire
 * @param familyId ID de la famille
 * @returns Message de confirmation
 */
export const transferFamilyOwnership = async (
  newOwnerId: string,
  familyId: string
) => {
  try {
    const response = await axios.post(
      `${BASE_URL_API}/family/${familyId}/transferOwnership`,
      { newOwnerId },
      HEADERS
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors du transfert de propriété:", error);
    throw error;
  }
};

/**
 * Met à jour les informations d'une maison
 * @param houseId ID de la maison à mettre à jour
 * @param houseData Données à mettre à jour (nom, adresse)
 * @returns La maison mise à jour
 */
export const updateHouse = async (
  houseId: string,
  houseData: { name: string; address?: string }
) => {
  try {
    const response = await axios.patch(
      `${BASE_URL_API}/house/${houseId}`,
      houseData,
      {
        headers: { authorization: localStorage.getItem("authorization") },
      }
    );
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la mise à jour de la maison:", error);
    throw error;
  }
};

/**
 * Met à jour le profil d'un utilisateur
 * @param userId ID de l'utilisateur à mettre à jour
 * @param data FormData contenant les données à mettre à jour (username, email, avatar, etc.)
 * @returns Les données de l'utilisateur mis à jour et le token si le serveur en renvoie un
 */
export const updateUserProfile = async (
  userId: string,
  data: FormData
): Promise<{ user: UserType; token?: string }> => {
  try {
    const response = await axios.patch(`${BASE_URL_API}/${userId}`, data, {
      headers: {
        "Content-Type": "multipart/form-data",
        authorization: localStorage.getItem("authorization"),
      },
    });

    // Structure de réponse normalisée
    let result: { user: UserType; token?: string };

    if (response.data && response.data.token) {
      // Si le serveur renvoie un utilisateur et un token
      result = {
        user: response.data.user || response.data,
        token: response.data.token,
      };

      // Mise à jour du token dans le localStorage
      setAuthToken(response.data.token);
    } else {
      // Si le serveur ne renvoie que l'utilisateur
      result = {
        user: response.data,
      };
    }

    return result;
  } catch (error) {
    console.error("Erreur lors de la mise à jour du profil:", error);
    throw error;
  }
};

/**
 * Recherche des utilisateurs par nom ou email
 * @param query Terme de recherche
 * @param exactMatch Si true, recherche exactement l'ID passé (pour chercher un utilisateur précis)
 * @returns Liste des utilisateurs correspondants
 */
export const searchUsers = async (
  query: string,
  exactMatch: boolean = false
): Promise<FamilyMember[]> => {
  try {
    // Si c'est une recherche exacte (par ID), utiliser getUserById directement
    if (exactMatch) {
      const user = await getUserById(query);
      return user ? [user as FamilyMember] : [];
    }

    // Sinon, faire une recherche par nom/email
    const response = await axios.get(`${BASE_URL_API}/search`, {
      params: { query },
      headers: { authorization: localStorage.getItem("authorization") },
    });
    return response.data;
  } catch (error) {
    console.error("Erreur lors de la recherche d'utilisateurs:", error);
    // En cas d'erreur, essayer de récupérer les membres de la famille
    try {
      const familyDetails = await getFamilyDetails();
      if (familyDetails && familyDetails.members) {
        return familyDetails.members.filter(
          (member: FamilyMember) =>
            member.username.toLowerCase().includes(query.toLowerCase()) ||
            member.email.toLowerCase().includes(query.toLowerCase())
        );
      }
    } catch (innerError) {
      console.error(
        "Échec du fallback sur les membres de la famille:",
        innerError
      );
    }
    return [];
  }
};
